import { TrendingDown, X, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { apiClient, handleApiError } from "@/lib/api-client";
import { authClient } from "@/lib/auth-client";

// Simulate scarcity with a "spots left" counter
function useSpotsLeft() {
	const [spotsLeft, setSpotsLeft] = useState<number | null>(null);

	useEffect(() => {
		// Get or initialize spots count
		const stored = localStorage.getItem("blackfriday-spots-left");
		const initial = stored ? parseInt(stored, 10) : 47; // Start at 47 spots
		setSpotsLeft(initial);

		// Randomly decrease spots every 30-90 seconds to create urgency
		const interval = setInterval(
			() => {
				setSpotsLeft((prev) => {
					if (prev === null || prev <= 12) return prev; // Don't go below 12
					const newCount = prev - 1;
					localStorage.setItem("blackfriday-spots-left", String(newCount));
					return newCount;
				});
			},
			Math.random() * 60000 + 30000,
		); // Random 30-90 seconds

		return () => clearInterval(interval);
	}, []);

	return spotsLeft;
}

export function BlackFridayBanner() {
	const [isVisible, setIsVisible] = useState(false);
	const [isCreatingSession, setIsCreatingSession] = useState(false);
	const spotsLeft = useSpotsLeft();
	const { data: session } = authClient.useSession();

	useEffect(() => {
		// Black Friday promotion ends December 3rd, 2025
		const deadline = new Date("2025-12-03T23:59:59");
		if (new Date() > deadline) {
			setIsVisible(false);
			return;
		}

		// Don't show banner if user has dismissed it OR has entitlement
		const dismissed = localStorage.getItem("blackfriday-banner-dismissed");
		const hasEntitlement = session?.user?.isBroadcasterEntitled;

		if (!dismissed && !hasEntitlement) {
			setIsVisible(true);
		}
	}, [session]);

	const handleDismiss = () => {
		setIsVisible(false);
		localStorage.setItem("blackfriday-banner-dismissed", "true");
	};

	const handleClaimSpot = async () => {
		// No auth check - supports both authenticated and guest checkout
		setIsCreatingSession(true);
		try {
			// Check for coupon override in localStorage (for testing)
			const couponOverride = localStorage.getItem("blackfriday-coupon-override");
			const couponCode = couponOverride || "BLACKFRIDAY25";

			const response = await apiClient.checkout.createSession({ couponCode });
			// Redirect to Stripe checkout
			window.location.href = response.sessionUrl;
		} catch (error) {
			handleApiError(error, "Failed to create checkout session");
			setIsCreatingSession(false);
		}
	};

	if (!isVisible) return null;

	const isLowStock = spotsLeft !== null && spotsLeft < 20;

	return (
		<div className="sticky top-0 z-50 relative bg-[#ff9500] text-black border-b border-black">
			<div className="relative mx-auto max-w-full px-4 py-3 sm:px-6 lg:px-8">
				<div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-center sm:text-left">
					{/* Badge */}
					<div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-[#ff9500] text-2xl font-bold uppercase tracking-wider font-serif">
						<Zap className="w-3 h-3 fill-current" />
						Black Friday
					</div>

					{/* Message */}
					<div className="flex-1 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 font-serif">
						<div className="flex items-center gap-2">
							<span className="text-sm sm:text-base font-bold">
								Lifetime Deal
							</span>
							<span className="line-through text-black/60 text-sm font-semibold">
								$119
							</span>
							<span className="text-black font-extrabold text-xl sm:text-2xl">
								$79
							</span>
						</div>

						{spotsLeft !== null && (
							<>
								<span className="hidden sm:inline text-black/40 font-bold">
									|
								</span>
								<div
									className={`flex items-center gap-2 ${isLowStock ? "animate-pulse" : ""}`}
								>
									{isLowStock && (
										<TrendingDown className="w-4 h-4 text-red-600" />
									)}
									<span className="text-sm font-semibold">
										<span
											className={`font-extrabold ${isLowStock ? "text-red-600" : "text-black"}`}
										>
											{spotsLeft} spots
										</span>{" "}
										<span className="text-black/80">left at this price</span>
									</span>
								</div>
							</>
						)}
					</div>

					{/* CTA Button */}
					<button
						type="button"
						onClick={handleClaimSpot}
						disabled={isCreatingSession}
						className="inline-flex items-center justify-center px-6 py-2.5 bg-black text-[#ff9500] font-bold text-xl hover:bg-black/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 whitespace-nowrap font-serif"
					>
						{isCreatingSession ? "Loading..." : "Go Live Now"}{" "}
						<span className="bg-red-600 ml-4 animate-pulse rounded-full w-4 h-4"></span>
					</button>

					{/* Close Button */}
					<button
						type="button"
						onClick={handleDismiss}
						className="absolute top-2 right-2 sm:relative sm:top-0 sm:right-0 p-1 hover:bg-black/20 transition-colors duration-200"
						aria-label="Dismiss banner"
					>
						<X className="w-4 h-4 text-black/60 hover:text-black" />
					</button>
				</div>
			</div>
		</div>
	);
}
